import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useDiagnostic } from "@/components/landing/DiagnosticContext";
import { QUESTIONS, INDUSTRIES } from "@/data/content";
import { QuestionStep } from "./QuestionStep";
import { ContactStep } from "./ContactStep";
import { ResultStep } from "./ResultStep";

const TOTAL = QUESTIONS.length + 1;

export const DiagnosticModal = () => {
  const { isOpen, close, answers, setAnswer } = useDiagnostic();
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen || result) return;
    const first = QUESTIONS.findIndex((q) => !answers[q.key]);
    setStep(first === -1 ? QUESTIONS.length : first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const q = QUESTIONS[step];
  const canNext = q ? Boolean(answers[q.key]) : false;
  const progress = result ? 100 : Math.round((step / TOTAL) * 100);

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="diagnostic-modal" data-lenis-prevent
        className="max-h-[92vh] w-[calc(100vw-24px)] max-w-2xl overflow-y-auto rounded-2xl border-slate-200 bg-white p-0 sm:rounded-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 px-6 pb-4 pt-6 backdrop-blur">
          <div className="flex items-center justify-between pr-8 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#0E9384]">
            <span data-testid="quiz-step-indicator">{result ? "Your result" : step < QUESTIONS.length ? `Question ${step + 1} of ${QUESTIONS.length}` : "Where should we send your guide?"}</span>
            <span className="text-slate-500">{progress}%</span>
          </div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-[#0E9384] to-[#2563EB]" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
          </div>
          <DialogTitle className="sr-only">AI Project Readiness Diagnostic</DialogTitle>
          <DialogDescription className="sr-only">Five short questions to suggest a provisional starting route.</DialogDescription>
        </div>
        <motion.div key={result ? "r" : step} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }} className="px-6 pb-6 pt-5">
          {result ? (
            <ResultStep result={result} onClose={close} />
          ) : q ? (
            <QuestionStep q={q} value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)}>
              {q.key === "area" && (
                <div className="mt-5">
                  <p className="text-[13px] font-semibold text-slate-700">Industry or domain (optional)</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {INDUSTRIES.map((ind) => (
                      <button key={ind} data-testid={`quiz-industry-${ind.split(" ")[0].toLowerCase()}`} onClick={() => setAnswer("industry", answers.industry === ind ? "" : ind)}
                        className={`rounded-full border px-3 py-1.5 text-[13px] transition-colors ${answers.industry === ind ? "border-[#0E9384] bg-teal-50 text-[#0B1B33]" : "border-slate-200 text-slate-700 hover:border-slate-400"}`}>
                        {ind}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {q.key === "goal" && (
                <OptionalText label="Optional: What kind of work problem would you like to explore?" hint="Do not include confidential employer, client, customer, or personal information."
                  value={answers.problem_idea} onChange={(v) => setAnswer("problem_idea", v)} testId="quiz-problem-idea" />
              )}
              {q.key === "hours" && (
                <OptionalText label="Optional: If you have a project in mind, describe the first small result you could complete in seven days." hint="One sentence is enough."
                  value={answers.first_result} onChange={(v) => setAnswer("first_result", v)} testId="quiz-first-result" />
              )}
            </QuestionStep>
          ) : (
            <ContactStep answers={answers} onDone={setResult} />
          )}
          {!result && (
            <div className="mt-6 flex items-center justify-between">
              <button data-testid="quiz-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)}
                className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-[14px] font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-0">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {q && (
                <button data-testid="quiz-next-btn" disabled={!canNext} onClick={() => setStep(step + 1)}
                  className="btn-teal flex items-center gap-2 rounded-lg px-6 py-3 text-[15px] font-semibold disabled:cursor-not-allowed disabled:opacity-40">
                  Continue <ArrowRight className="arrow h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

const OptionalText = ({ label, hint, value, onChange, testId }) => (
  <div className="mt-5">
    <label className="text-[13px] font-semibold text-slate-700">{label}</label>
    <input data-testid={testId} value={value || ""} maxLength={300} onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-[14px] outline-none transition-colors focus:border-[#0E9384] focus:ring-2 focus:ring-teal-100" />
    <p className="mt-1 text-[12px] text-slate-500">{hint}</p>
  </div>
);
